import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { cap, stagger } from '../utils/format';
import { useRawProperty } from '../utils/hooks';
import { PATHS, tourCta, useSiteFlags } from '../utils/site';

interface NextStepProps {
  /** Page aliases for "Keep exploring", in loop order; the first two this property has are shown. */
  explore?: string[];
  /** Buttons: Explore Floor Plans + tour (default), or tour + Apply Now on the floor plans page itself. */
  primary?: 'plans' | 'tour';
  /** Lead default: the homes summary, tour then apply, compare layouts, browse homes, or bedrooms + ready now. */
  lead?: 'summary' | 'apply' | 'layouts' | 'browse' | 'homes';
  /** Heading default: the theme line, "See {community} in person", or "Ready to find your new home?". */
  heading?: 'default' | 'visit' | 'ready';
  /** Background: paper (default) or the surface band. */
  tone?: 'paper' | 'surface';
}

const plansCtaDef: SlotGroupDefinition = {
  label: 'Floor Plans Button',
  fields: {
    label: { label: 'Button Text', type: 'text', defaultValue: 'Explore Floor Plans' },
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const tourCtaDef: SlotGroupDefinition = {
  label: 'Tour Button',
  description: 'Without a self-guided tour URL on the property, the default button becomes Contact Us.',
  fields: {
    label: { label: 'Button Text', type: 'text', defaultValue: 'Book Self-Guided Tour' },
    url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
    vibesite_event: { label: 'Tracked Event', type: 'event', defaultValue: 'self_guided_tour_clicked' },
  },
};

const applyCtaDef: SlotGroupDefinition = {
  label: 'Apply Now Button',
  description: 'Shown next to the tour button on the floor plans page.',
  fields: {
    label: { label: 'Button Text', type: 'text', defaultValue: 'Apply Now' },
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_apply_now_url',
      suggestedFields: ['property_apply_now_url', 'property_online_leasing_url'],
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
    vibesite_event: { label: 'Tracked Event', type: 'event', defaultValue: 'apply_now_clicked' },
  },
};

const LOOP: Record<string, [string, string, string]> = {
  gallery: ['gallery', 'Gallery', 'Photos of the homes and the community'],
  'virtual-tours': ['virtualTours', 'Virtual Tours', 'Walk through a home in 3D'],
  neighborhood: ['neighborhood', 'Neighborhood', 'Schools, parks and errands nearby'],
  reviews: ['reviews', 'Reviews', 'What residents say'],
  faqs: ['faqs', 'FAQs', 'Qualifications, pets, and policies'],
  contact: ['contact', 'Contact', 'Talk with the leasing team'],
  'floor-plans': ['floorPlans', 'Floor Plans', 'Every plan with pricing and availability'],
  amenities: ['amenities', 'Amenities', 'Your home and the community'],
};

const HIGHLIGHTS: Array<[string, RegExp]> = [
  ['the pool', /\bpool\b/i],
  ['the dog park', /dog park|dog run|bark park|paw park/i],
  ['the clubhouse', /clubhouse|club house/i],
  ['the fitness center', /fitness|gym/i],
  ['the playground', /playground|tot lot/i],
];

/** "The pool, the dog park and what's in every home": the first two community highlights the property has. */
function amenitiesDesc(p: Record<string, unknown>): string {
  const titles = ((p.amenities as Array<{ title?: string; group?: string }>) || []).filter((a) => a.group !== 'Apartment').map((a) => a.title || '');
  const picks = HIGHLIGHTS.filter(([, rx]) => titles.some((t) => rx.test(t))).map(([w]) => w).slice(0, 2);
  if (!picks.length) return '';
  return cap(`${picks.join(', ')} and what’s in every home`);
}

/** The schema's homes summary needs floor plans or photos; before either exists the lead invites interest instead. */
const COMING_SOON_LEAD = `<p><span data-type="field-token" data-field="property_name"></span> is coming soon. Join the interest list to hear first about pricing, floor plans and move-in dates.</p>`;

const TITLES: Record<NonNullable<NextStepProps['heading']>, string | undefined> = {
  default: undefined,
  visit: '<p>See <span data-type="field-token" data-field="property_name"></span> in person</p>',
  ready: '<p>Ready to find your new home?</p>',
};

const LEADS: Record<NonNullable<NextStepProps['lead']>, string | undefined> = {
  summary: undefined,
  apply: '<p>Tour an available home on your own schedule, then apply online.</p>',
  layouts: '<p>Compare the floor plans at <span data-type="field-token" data-field="property_name"></span>, then tour an available home on your own schedule.</p>',
  browse: '<p>Explore available homes, compare floor plans, and find the right fit for you.</p>',
  homes: '<p>Homes with <span data-type="field-token" data-field="property_bedrooms_range"></span> bedrooms at <span data-type="field-token" data-field="property_name"></span>. <span data-type="field-token" data-field="property_available_units"></span> ready to move in now.</p>',
};

export function NextStep({ explore = ['reviews', 'faqs', 'floor-plans'], primary = 'plans', lead = 'summary', heading = 'default', tone = 'paper' }: NextStepProps) {
  const p = useRawProperty();
  const flags = useSiteFlags();
  const plans = useSlotGroup('nextstep.plans_cta', plansCtaDef);
  const tour = useSlotGroup('nextstep.tour_cta', tourCtaDef);
  const apply = useSlotGroup('nextstep.apply_cta', applyCtaDef);
  const tourLink = tourCta(tour, flags.selfTourUrl);
  const applyHref = apply.url || flags.applyUrl;
  const titleDefault = TITLES[heading];
  const preLeasing = !((p.floorplans as unknown[]) || []).length && !((p.photo_gallery as unknown[]) || []).length;
  const leadDefault = preLeasing && (lead === 'summary' || lead === 'homes') ? COMING_SOON_LEAD : LEADS[lead];

  const plansButton = (
    <EditableSlotGroup groupPath="nextstep.plans_cta" definition={plansCtaDef}>
      <a className="btn btn-primary" href={plans.url || PATHS.floorPlans} target={plans._target || '_self'}>
        {plans.label || 'Explore Floor Plans'} <span className="btn__arrow">→</span>
      </a>
    </EditableSlotGroup>
  );
  const tourButton = (
    <EditableSlotGroup groupPath="nextstep.tour_cta" definition={tourCtaDef}>
      <a
        className={`btn ${primary === 'tour' ? 'btn-primary' : 'btn-secondary'}`}
        href={tourLink.href}
        target={tourLink.isTour ? tour._target || '_self' : '_self'}
        data-vibesite-event={tourLink.isTour ? tour.vibesite_event || undefined : undefined}
      >
        {tourLink.label}
      </a>
    </EditableSlotGroup>
  );
  const applyButton = (
    <EditableSlotGroup groupPath="nextstep.apply_cta" definition={applyCtaDef}>
      {applyHref && (
        <a className="btn btn-secondary" href={applyHref} target={apply._target || '_self'} data-vibesite-event={apply.vibesite_event || undefined}>
          {apply.label || 'Apply Now'}
        </a>
      )}
    </EditableSlotGroup>
  );

  const keep = explore
    .filter((alias) => LOOP[alias] && flags.shows(alias))
    .slice(0, 2)
    .map((alias) => {
      const [key, name, desc] = LOOP[alias];
      return { href: PATHS[key as keyof typeof PATHS], name, desc: alias === 'amenities' ? amenitiesDesc(p) || desc : desc };
    });

  return (
    <section
      className={`flex items-end justify-between gap-24 px-[--gutter] py-28 max-[1199px]:flex-col max-[1199px]:items-stretch max-[1199px]:gap-12 max-[767px]:gap-6 max-[767px]:py-14${tone === 'surface' ? ' bg-[--q-surface]' : ''}`}
    >
      <div className="flex w-[720px] min-w-0 flex-col gap-6 max-[1199px]:w-auto max-[767px]:gap-4" data-reveal>
        <RichTextContent path="nextstep.eyebrow" as="div" className="eyebrow" />
        <RichTextContent path="nextstep.title" as="h2" className="h1" defaultValue={titleDefault} />
        <RichTextContent
          path="nextstep.lead"
          as="p"
          className="m-0 max-w-[600px] text-lg leading-7 text-muted-foreground max-[767px]:text-[15px] max-[767px]:leading-[23px]"
          defaultValue={leadDefault}
        />
        <div className="flex gap-3 pt-2 max-[767px]:flex-col max-[767px]:gap-2.5">
          {primary === 'tour' ? (
            <>
              {tourButton}
              {applyButton}
            </>
          ) : (
            <>
              {plansButton}
              {tourButton}
            </>
          )}
        </div>
      </div>
      {keep.length > 0 && (
        <div className="flex w-[360px] shrink-0 flex-col border-t border-[--q-border] max-[1199px]:w-auto" data-reveal style={stagger(1)}>
          <RichTextContent path="nextstep.explore_label" as="div" className="label pb-2.5 pt-4" />
          <div>
            {keep.map((k) => (
              <a
                key={k.href}
                href={k.href}
                className="flex items-center justify-between gap-4 border-t border-[--q-border] py-4 no-underline last:border-b max-[767px]:py-3.5"
              >
                <span className="flex flex-col gap-0.5">
                  <span className="font-heading text-[22px] leading-7 max-[767px]:text-[20px] max-[767px]:leading-[26px]">{k.name}</span>
                  <span className="text-[13px] leading-[18px] text-muted-foreground">{k.desc}</span>
                </span>
                <span className="btn__arrow text-[20px] text-[--q-primary-on-paper]">→</span>
              </a>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'NextStep',
  displayName: 'Next Step',
  tokens: {
    'nextstep.eyebrow': { label: 'Eyebrow', type: 'richtext', defaultValue: '<p>Next step</p>' },
    'nextstep.title': { label: 'Title', type: 'richtext', defaultValue: '<p>Find the home that fits.</p>' },
    'nextstep.lead': {
      label: 'Lead',
      description:
        'A homes summary from property fields. Some pages swap in their own line (apply steps, layouts to compare), and a property with no floor plans or photos yet says it is coming soon.',
      type: 'richtext',
      defaultValue:
        '<p>Homes with <span data-type="field-token" data-field="property_bedrooms_range"></span> bedrooms and <span data-type="field-token" data-field="property_sqft_range"></span> square feet. <span data-type="field-token" data-field="property_available_units"></span> ready to move in now.</p>',
      generator: {
        prompt: 'A one- or two-sentence summary of the homes here: bedroom counts, the standout home feature, the starting rent, and how many are ready to move in.',
        context: 'Lead of the Next Step band at the bottom of every inner page, above Explore Floor Plans and Book Self-Guided Tour.',
        length: '1–2 sentences',
      },
    },
    'nextstep.explore_label': { label: 'Keep Exploring Label', type: 'richtext', defaultValue: '<p>Keep exploring</p>' },
  },
  slotGroups: {
    'nextstep.plans_cta': plansCtaDef,
    'nextstep.tour_cta': tourCtaDef,
    'nextstep.apply_cta': applyCtaDef,
  },
};

(NextStep as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default NextStep;
